import React from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";

const Consolidated = () => {
  return (
    <>
      <Helmet>
        <title>Consolidated Cargo | Trans World Shipping</title>
        <meta
          name="description"
          content="Efficient consolidated cargo solutions for your international shipping needs."
        />
        <link
          rel="canonical"
          href="https://transworldshippingcompany.com/services/consolidated-cargo"
        />
      </Helmet>

      <TheConsolidated>
        <div className="consolidated-hero">
          <div className="hero-overlay"></div>
          <div className="hero-content">
            <h1>Consolidated Cargo</h1>
            <p>
              Efficient and cost-effective shipping solutions for your
              business
            </p>
          </div>
        </div>

        <div className="consolidated-container">
          <div className="intro-section">
            <p>
              <strong>Consolidated Cargo services</strong> - combining smaller
              shipments into one container for smarter, more affordable
              shipping.
            </p>
          </div>

          <div className="commitment-section">
            <p>
              We group compatible cargo from multiple shippers into a single
              container, splitting the freight cost across everyone without
              compromising on transit speed, security, or handling quality.
            </p>
          </div>

          <div className="specifications-section">
            <p>
              Every consolidated shipment is carefully planned, packed, and
              documented so your goods are always individually tracked - even
              when they're sharing space with other cargo.
            </p>
          </div>

          <div className="equipment-section">
            <p>
              We use dedicated consolidation warehouses and load-optimization
              tools to keep freight moving on schedule with minimal handling.
            </p>
          </div>

          <div className="flexibility-section">
            <p>
              Consolidated cargo is ideal for businesses that ship smaller or
              irregular volumes and still want full-container reliability at
              a fraction of the cost.
            </p>
          </div>

          <div className="dedication-section">
            <p>
              Our consolidation team coordinates cut-off dates, customs
              paperwork, and delivery schedules for every shipper in the
              container, start to finish.
            </p>
          </div>

          <div className="services-list">
            <h3>Consolidated cargo service includes:</h3>
            <div className="service-items">
              <span className="service-tag">Door - Door service</span>
              <span className="service-tag">Shared container cost savings</span>
              <span className="service-tag">Individual shipment tracking</span>
              <span className="service-tag">Customs documentation</span>
              <span className="service-tag">Insurance</span>
            </div>
          </div>
        </div>
      </TheConsolidated>
    </>
  );
};

const TheConsolidated = styled.div`
  width: 100%;
  background: #ffffff;

  .consolidated-hero {
    position: relative;
    width: 100%;
    height: 40vh;
    min-height: 300px;
    background: linear-gradient(135deg, #0b1e33 0%, #0a1a2c 100%);

    .hero-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: radial-gradient(
        circle at 30% 50%,
        rgba(212, 175, 55, 0.1) 0%,
        transparent 70%
      );
    }

    .hero-content {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      color: white;
      width: 100%;
      padding: 0 2rem;
      z-index: 1;

      h1 {
        font-size: 3rem;
        font-weight: 700;
        margin-bottom: 1rem;
      }

      p {
        font-size: 1.2rem;
        opacity: 0.9;
      }
    }
  }

  .consolidated-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 4rem 2rem;
  }

  .intro-section {
    text-align: center;
    margin-bottom: 2rem;

    p {
      font-size: 1.3rem;
      color: #0b1e33;
      font-weight: 600;

      strong {
        color: #d4af37;
      }
    }
  }

  .commitment-section {
    background: linear-gradient(135deg, #0b1e33 0%, #0a1a2c 100%);
    padding: 2rem;
    border-radius: 12px;
    text-align: center;
    margin-bottom: 1.5rem;

    p {
      color: white;
      font-size: 1.1rem;
      line-height: 1.7;
    }
  }

  .specifications-section,
  .equipment-section,
  .flexibility-section,
  .dedication-section {
    background: #f8f9fa;
    padding: 1.5rem;
    border-radius: 12px;
    text-align: center;
    margin-bottom: 1.5rem;
    border: 1px solid #e2e8f0;

    p {
      font-size: 1rem;
      color: #4a5568;
      line-height: 1.6;
    }
  }

  .services-list {
    background: #f8f9fa;
    padding: 2rem;
    border-radius: 12px;
    text-align: center;
    margin-top: 1rem;
    border: 1px solid #e2e8f0;

    h3 {
      font-size: 1.2rem;
      color: #0b1e33;
      margin-bottom: 1.5rem;
      font-weight: 600;
    }

    .service-items {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 1rem;

      .service-tag {
        background: white;
        padding: 0.6rem 1.2rem;
        border-radius: 30px;
        font-size: 0.9rem;
        color: #0b1e33;
        border: 1px solid #d4af37;
        transition: all 0.3s ease;

        &:hover {
          background: #d4af37;
          color: white;
          transform: translateY(-2px);
        }
      }
    }
  }

  @media screen and (max-width: 900px) {
    .consolidated-hero .hero-content h1 {
      font-size: 2.5rem;
    }

    .consolidated-container {
      padding: 3rem 1.5rem;
    }

    .intro-section p {
      font-size: 1.1rem;
    }

    .commitment-section p {
      font-size: 1rem;
    }
  }

  @media screen and (max-width: 768px) {
    .consolidated-hero {
      min-height: 250px;

      .hero-content h1 {
        font-size: 2rem;
      }

      .hero-content p {
        font-size: 1rem;
      }
    }

    .consolidated-container {
      padding: 2rem 1rem;
    }

    .intro-section p {
      font-size: 1rem;
    }

    .services-list .service-items {
      flex-direction: column;
      align-items: center;

      .service-tag {
        width: 100%;
        max-width: 250px;
        text-align: center;
      }
    }
  }

  @media screen and (max-width: 600px) {
    .consolidated-hero .hero-content h1 {
      font-size: 1.6rem;
    }

    .commitment-section p,
    .specifications-section p,
    .equipment-section p,
    .flexibility-section p,
    .dedication-section p {
      font-size: 0.95rem;
    }

    .services-list h3 {
      font-size: 1rem;
    }
  }

  @media screen and (max-width: 480px) {
    .consolidated-hero .hero-content h1 {
      font-size: 1.3rem;
    }

    .consolidated-hero .hero-content p {
      font-size: 0.9rem;
    }
  }
`;

export default Consolidated;
